import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { Container, Header, Segment, Divider, Icon, List, Grid, Card } from 'semantic-ui-react';
import './Playbook.css';

class BuildingTheMessage extends Component {
    render() {
        return (
            <Container className="playbook-section-container">
                <h1 className="ui icon center aligned header playbook-branded-header" style={{fontFamily: 'Montserrat', textTransform: 'uppercase', color: 'rgb(35, 37, 58)', fontWeight: 600, fontSize: '38px', marginBottom: '-9px'}}>
                    <i aria-hidden="true" className="icon">
                        <img src="/static/media/logo-icon.7bb8994723bbdcd674f1.png" alt="logo-icon" style={{height: '68px', marginBottom: '-15px'}} />
                    </i>
                    Building The Message
                </h1>
                <div className="ui divider"></div>

                <Segment className="playbook-content-segment">
                    <Header as="h2" color="blue">Sales Deck - Explained</Header>
                    
                    <Header as="h3">Introduction</Header>
                    <p>
                        By now, you should have read the "Messaging: Explained" document that walks through how to think through your company's messaging. 
                        This document follows that one and guides you through how to create a PowerPoint presentation that allows you or your team to 
                        convey your company messaging. The PowerPoint will allow you to talk about your company in a manner that conveys the information 
                        you are completing in the "Company Background" and "Offerings" sections of the Multiply Application.
                    </p>

                    <p>The high-level sections of the pitch deck should convey:</p>
                    <List bulleted>
                        <List.Item>Who your company is</List.Item>
                        <List.Item>Who your company serves</List.Item>
                        <List.Item>The problem(s) your company solves</List.Item>
                        <List.Item>The solution your company provides</List.Item>
                        <List.Item>How you engage</List.Item>
                    </List>

                    <p>
                        We will utilize the Multiply deck as an example for you to pattern yours after. The deck matures with every success, process change, 
                        or new offering. Further, we have proven that the format works whether your company is a pre-revenue startup or a publicly-traded 
                        company selling complex energy solutions to international organizations—or anything in between.
                    </p>

                    <p>
                        It's simple. You tell a prospect about your company, formulate asks, and earn the right to do something complicated. The complicated 
                        thing can be solutioning. You have to sell first—or MUCH more simply put, you have to tell the prospects who you are and what you do.
                    </p>

                    <Divider />

                    <Header as="h3" color="blue">Creating Corporate Credibility</Header>
                    <p>
                        When you or your team enters an encounter, the first thing you will do is create some level of personal credibility. This is 
                        generally done through personal introductions. Then very quickly, you want to—or you want your team members to—transition from 
                        individual credibility to corporate credibility.
                    </p>

                    <Segment color="yellow">
                        <Header as="h4">Important Note</Header>
                        <p>
                            In general, you are presenting a risk proposition to the buyer. The buyer assesses the risk of the purchase compared with 
                            the reward of the solution. If you jump straight to your solution and skip the credibility-building process, then you are 
                            NOT helping the client reduce the risk of purchase.
                        </p>
                    </Segment>

                    <p>
                        If you look at the Multiply pitch deck accompanying this package, you will see that slides 2, 3, and 4 are dedicated to 
                        creating credibility for Multiply.
                    </p>

                    <Grid stackable columns={3}>
                        <Grid.Column>
                            <Card>
                                <Card.Content>
                                    <Card.Header>Mission (Slide 2)</Card.Header>
                                    <Card.Description>
                                        We start with our Mission. This allows us to talk through the Company Background, our founding, years in services, 
                                        the basis from which the Multiply process was created, and how the company has matured over the years.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content>
                                    <Card.Header>Framework (Slide 3)</Card.Header>
                                    <Card.Description>
                                        The third slide allows us to speak directly to how Multiply solves problems. We DO NOT spend a lot of time 
                                        explaining details here. We are just setting up the discussion by showing we have a defined methodology.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>

                        <Grid.Column>
                            <Card>
                                <Card.Content>
                                    <Card.Header>Client Base (Slide 4)</Card.Header>
                                    <Card.Description>
                                        We spend very little time here. The point of this slide is not to get into specifics about each customer but 
                                        to show that we worked for them and they utilized our process.
                                    </Card.Description>
                                </Card.Content>
                            </Card>
                        </Grid.Column>
                    </Grid>

                    <Divider />

                    <Header as="h3" color="blue">Multiply Sales Management Framework (Slide 3)</Header>
                    <p>We explain that:</p>
                    <List bulleted>
                        <List.Item>We have built our own proprietary technology that accompanies the methodology.</List.Item>
                        <List.Item>We have multiple engagement models that allow companies to engage in a variety of ways.</List.Item>
                        <List.Item>We have utilized this exact methodology successfully in publicly traded companies and pre-revenue startups across the country.</List.Item>
                    </List>

                    <p>
                        We are a SaaS company. We sell software, but we also provide options for services around it. Oh, and we make sure we tell 
                        the prospects that WE ARE NOT CRM. You will see in the "Messaging: Explained" document how we talk about the competition and 
                        position it in a box that places our company in a different, more favorable box.
                    </p>

                    <Segment color="blue">
                        <Header as="h4">Multiply Tip</Header>
                        <p>
                            We are using our slide deck as a template. If you are a consumer-based product looking at a B2C2B model, or if you have 
                            a variety of users on a product that don't group up nicely by customer, then you can still convey the same points.
                        </p>
                        <p>
                            If you are a small- or medium-sized company, then you are trying to present as bigger and more stable. Things that convey 
                            stability and credibility that we haven't listed but have used in our past and/or with clients are:
                        </p>
                        <List bulleted>
                            <List.Item>Market locations presented on a map</List.Item>
                            <List.Item>Funding to date (or lack thereof if bootstrapping)</List.Item>
                            <List.Item>Number of users</List.Item>
                            <List.Item>Revenue numbers</List.Item>
                            <List.Item>Verticals served</List.Item>
                            <List.Item>Number of employees</List.Item>
                        </List>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="blue">Differentiation</Header>
                    <p>
                        Slides 5, 6, and 7 are dedicated to differentiating Multiply and our approach to creating a sales process and salespeople 
                        by stating the opportunities that exist with the traditional sales management.
                    </p>

                    <p>
                        Here we describe how sales management generally starts with CRM tools and activity-based management. That is fine and necessary, 
                        but we have proven that to be incomplete. We state the problems through slides 5 and 6. In short, the companies employing a 
                        typical sales process make massive assumptions and critical assumptions for a variety of reasons. The critical assumption is that 
                        salespeople know:
                    </p>

                    <List bulleted>
                        <List.Item><strong>What to say</strong></List.Item>
                        <List.Item><strong>When to say it</strong></List.Item>
                        <List.Item><strong>How to say it</strong></List.Item>
                    </List>

                    <p>This is EXACTLY where we are highlighting the Addressable Problem to the prospect.</p>

                    <Segment color="orange">
                        <Header as="h4">The 80/20 Rule</Header>
                        <p>
                            That leads to the 80/20 rule of sales (80% of sales comes from 20% of the organization) that is depicted in slide 5, 
                            where some unicorns figure it out but others are left to flounder. Slide 6 gets to why this premise exists based on 
                            the incomplete nature of sales management.
                        </p>
                    </Segment>

                    <p>
                        Slide 7 demonstrates how we solve the problem. We are just now starting to solution. We have built a pattern of a conversation 
                        that happens OVER AND OVER AND OVER again. This pattern can be developed and taught, and as a result, you can drive value out 
                        of your sales team.
                    </p>

                    <Divider />

                    <Header as="h3" color="blue">The Offerings</Header>
                    <p>
                        Slide 8 is dedicated to the solutions (or what we refer to as the offerings in the Multiply Application). If we are in an 
                        introductory meeting, then our goal is to introduce ALL of the offerings. Based on the prospect profile, we may already know 
                        which offering the prospect is most likely to buy.
                    </p>

                    <p>This does a few things:</p>
                    <List numbered>
                        <List.Item>
                            <strong>1st</strong> – Multiply Rule #2 is to assume your prospect knows nothing. The extension of that is that we can't 
                            assume what they need. It cannot hurt if we spend 2 minutes talking about an offering
                        </List.Item>
                        <List.Item>
                            <strong>2nd</strong> – It drives interest in your company. This prospect may not need an offering, but hearing about it may 
                            prompt him or her to recommend you to a coworker, friend, etc.
                        </List.Item>
                        <List.Item>
                            <strong>3rd</strong> – It creates credibility and depth.
                        </List.Item>
                    </List>

                    <Segment color="teal">
                        <Header as="h4">Multiply Tip</Header>
                        <p>
                            We demo software as a part of our presentation. We DO NOT get into the finer points of the software such as admin functions. 
                            Prospects will assume the software works in this setting; otherwise you would not be talking to them. Therefore, avoid getting 
                            into the weeds. Demonstrate the key functions of the software. Avoid the minutia of the administration functions, such as 
                            adding users, roles, the finer points of accounting, etc.
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="blue">How We Engage</Header>
                    <p>
                        Once we have reviewed the offerings with the prospects, we come back to the presentation and very clearly lay out the engagement 
                        models for Multiply and what is included.
                    </p>

                    <p>
                        We DO NOT shy away from showing pricing. This is not a legal document. We are not committed to anything, and if the client has 
                        a need that fits the options perfectly, then it is very easy. But often they don't, and as a need shifts, the price can shift 
                        as well. But the point is that we are qualifying them out.
                    </p>

                    <p>
                        By the time we get to this slide, we have talked for 15-20 minutes. Now is the time to execute our predetermined "asks." The 
                        asks are detailed in the "Messaging: Explained" document, so we will not rehash here. The point is to now let the prospect talk, 
                        but we have set the tone for the conversation in such a way that they are talking about their company and their problems in the 
                        context of the conversation that we laid out.
                    </p>

                    <Segment color="green">
                        <Header as="h4">Key Insight</Header>
                        <p>
                            <strong>Sales messages are SIMPLE. Solutions are COMPLICATED.</strong>
                        </p>
                        <p>
                            This takes about 10 minutes. And we know the objection: My company is more complex than Multiply. It may be. However, we 
                            have helped very complex companies build the exact same model of slides and have seen a client employ it in one meeting with 
                            a $40B multinational company and walk out with a $200K+ initial audit which could lead to millions.
                        </p>
                    </Segment>

                    <Divider />

                    <Header as="h3" color="blue">Customer Pitch Deck vs. Investor Pitch Deck</Header>
                    <p>
                        As mentioned before, we deal with clients of all sizes. We like to say our offerings fit from incubator to enterprise. When we 
                        are dealing with the incubators, accelerators, and startups, we get lots of questions about how the customer pitch fits with 
                        the investor pitch.
                    </p>

                    <Header as="h4">Simple answer: THEY DO NOT GO TOGETHER.</Header>

                    <p>
                        Customer and investors care about very different things. Customers care about problems, while investors care about making money. 
                        So while the two pitch decks can share some of the same information, the messages portrayed are VERY different.
                    </p>

                    <List bulleted>
                        <List.Item>
                            <strong>Customers</strong> really don't care about your market size, opportunity, your team credentials and organizational 
                            structure, and how rich you are going to be when your company explodes. Customers care about their problems and if your 
                            company is credible and capable of solving them.
                        </List.Item>
                        <List.Item>
                            <strong>Investors</strong> care about market opportunity, team credentials, organizational structure, and financial projections.
                        </List.Item>
                    </List>

                    <p>
                        Here's the point: Don't confuse the two presentations. They are very different.
                    </p>

                    <Divider />

                    <Header as="h3" color="blue">Conclusion</Header>
                    <p>
                        The point of this document is to help you think through your pitch deck. When we present this deck, we are doing much more than 
                        presenting our solution. We are actually earning the right to present the solution and then ask questions to determine if our 
                        solution fits into the prospect's set of needs.
                    </p>

                    <p>
                        This process becomes VERY easy and is VERY effective. Create corporate credibility. Differentiate your company. Describe your 
                        offerings in ways that are easy to understand, and earn the right to do something complicated which leads directly to the Multiply 
                        Proposal Module.
                    </p>
                </Segment>
            </Container>
        );
    }
}

export default observer(BuildingTheMessage);